import { supabase } from "@/integrations/supabase/client";

/**
 * Data layer for the drawing-compare tool's pickers. The drawing bytes
 * themselves (artifact renditions + original DWGs) live in ./drawingSource.
 */

// ---------------------------------------------------------------------------
// A DWG drawing in a project — every version, not just the current one.
// ---------------------------------------------------------------------------
export interface DwgDrawing {
  id: number;
  filename: string;
  version: number | null;
  version_group: string | null;
  /** Nullable in the schema. Normalised where it is read, never asserted
   *  away — a null flowing into latest-version filtering is a silent falsy. */
  is_latest_version: boolean | null;
  project_id: number | null;
  folder_id: number | null;
  size: number | null;
  created_at: string | null;
}

export async function fetchDwgDrawings(orgId: string, projectId: number): Promise<DwgDrawing[]> {
  const { data, error } = await supabase
    .from("documents")
    .select(
      "id, filename, version, version_group, is_latest_version, project_id, folder_id, size, created_at"
    )
    .eq("org_id", orgId)
    .eq("project_id", projectId)
    .eq("container_type", "dwg")
    .eq("is_dwg_child", false)
    .eq("is_deleted", false)
    // Every version (not just `is_latest_version`): the picker nests superseded
    // revisions under the current one and lets the user compare across them,
    // the same way fetchIfcModelsForBuilding feeds the 3D viewer's picker.
    // `filename asc` keeps the top-level list alphabetical; `version desc`
    // makes each chain arrive newest-first, which groupCandidatesIntoChains
    // relies on to pick a head when `is_latest_version` is unset.
    .order("filename", { ascending: true })
    .order("version", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as DwgDrawing[];
}

// ---------------------------------------------------------------------------
// Seed documents for the URL-ids entry (/tools/drawings?ids=…): just enough to
// point the project/drawing/version pickers at the requested documents. RLS
// scopes visibility, same as every other read here.
// ---------------------------------------------------------------------------
export interface SeedDoc {
  id: number;
  project_id: number | null;
  version_group: string | null;
}

export async function fetchSeedDocs(orgId: string, ids: number[]): Promise<SeedDoc[]> {
  // org filter matters beyond isolation: every downstream picker fetch is
  // scoped to the CURRENT org, so a doc from the user's other org could seed a
  // project the pickers can never load — filter it out here so it reports as
  // not found instead of a silently empty tool.
  const { data, error } = await supabase
    .from("documents")
    .select("id, project_id, version_group")
    .in("id", ids)
    .eq("org_id", orgId)
    .eq("is_deleted", false);
  if (error) throw new Error(error.message);
  return (data ?? []) as SeedDoc[];
}

// ---------------------------------------------------------------------------
// Drawing reference for a compare side — a project document. Kept as a tagged
// shape so a future standalone-upload source can slot back in as another
// variant.
// ---------------------------------------------------------------------------
export type DrawingRef = { source: "project"; documentId: number };

export const refKey = (r: DrawingRef): string => `doc:${r.documentId}`;
